import React, { useState, useEffect } from 'react';
import animalService from '../../../services/animalService';
import vaccinationService from '../../../services/vaccinationService';
import productionService from '../../../services/productionService';
import expenditureService from '../../../services/expenditureService';
import customerService from '../../../services/customerService';
import financeService from '../../../services/financeService';

const formatCurrency = (amount) => {
  const num = Math.round(parseFloat(amount || 0));
  return 'Rs ' + num.toLocaleString('en-US');
};

// Format a Date as YYYY-MM-DD using LOCAL components (avoids the UTC shift
// that toISOString() introduces, e.g. 1st of month rolling back to previous month).
const toLocalYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const now = new Date();
const today = toLocalYMD(now);
const monthStart = toLocalYMD(new Date(now.getFullYear(), now.getMonth(), 1));

// True if a date value (string or ISO) falls within the current calendar month
const isThisMonth = (dateVal) => {
  if (!dateVal) return false;
  const d = dateVal.includes('T') ? dateVal.split('T')[0] : String(dateVal).substring(0, 10);
  return d >= monthStart && d <= today;
};

const DashboardTab = ({ setActiveTab }) => {
  const [stats, setStats] = useState({
    totalAnimals: 0,
    milkingAnimals: 0,
    checkupDue: 0,
    overdueVaccinations: 0,
    todayProduction: 0,
    todayCowProduction: 0,
    todayBuffaloProduction: 0,
    monthlyRevenue: 0,
    monthlyExpenditure: 0,
    totalOutstanding: 0,
    netProfit: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [
        animalStats,
        milkingAnimals,
        vaccOverdue,
        allProductions,
        allExpenditures,
        customers,
        allSales,
      ] = await Promise.all([
        animalService.getStats(),
        animalService.getMilkingAnimals(),
        vaccinationService.getOverdue(),
        productionService.getAllProduction(),
        expenditureService.getAllExpenditures().catch(() => ({ data: [] })),
        customerService.getAllCustomers(),
        financeService.getAllMilkSales().catch(() => ({ data: [] })),
      ]);

      // Today's production
      const allProd = allProductions.data || [];
      const todayProd = allProd.filter(p =>
        p.date && p.date.split('T')[0] === today
      );
      const todayCow = todayProd
        .filter(p => p.animal_type === 'Cow')
        .reduce((sum, p) => sum + parseFloat(p.morning_production || 0) + parseFloat(p.evening_production || 0), 0);
      const todayBuffalo = todayProd
        .filter(p => p.animal_type === 'Buffalo')
        .reduce((sum, p) => sum + parseFloat(p.morning_production || 0) + parseFloat(p.evening_production || 0), 0);

      // Outstanding from customers (live balance — not a monthly figure)
      const allCustomers = customers.data || [];
      const totalOutstanding = allCustomers.reduce(
        (sum, c) => sum + parseFloat(c.outstanding_balance || 0), 0
      );

      // Monthly revenue — sum of THIS MONTH's milk sales only
      const monthlyRevenue = (allSales.data || [])
        .filter(s => isThisMonth(s.sale_date))
        .reduce((sum, s) => sum + parseFloat(s.total_amount || 0), 0);

      // Monthly expenditure — sum of THIS MONTH's expenditures only
      const monthlyExpenditure = (allExpenditures.data || [])
        .filter(e => isThisMonth(e.expense_date || e.date))
        .reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);

      setStats({
        totalAnimals: parseInt(animalStats.data?.total_animals) || 0,
        milkingAnimals: (milkingAnimals.data || []).length,
        checkupDue: parseInt(animalStats.data?.checkup_due_count) || 0,
        overdueVaccinations: vaccOverdue.count || 0,
        todayProduction: todayCow + todayBuffalo,
        todayCowProduction: todayCow,
        todayBuffaloProduction: todayBuffalo,
        monthlyRevenue,
        monthlyExpenditure,
        totalOutstanding,
        netProfit: monthlyRevenue - monthlyExpenditure,
      });

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
        <h2 style={{ color: '#666' }}>Loading dashboard...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
        <h2 style={{ color: '#e74c3c', marginBottom: '10px' }}>Error</h2>
        <p style={{ color: '#666' }}>{error}</p>
        <button onClick={fetchDashboardData} style={styles.retryButton}>🔄 Retry</button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#2c3e50', margin: 0 }}>
          Farm Overview
        </h2>
        <button onClick={fetchDashboardData} style={styles.refreshButton}>
          🔄 Refresh
        </button>
      </div>

      {/* ── SECTION 1: Animals ── */}
      <div style={styles.sectionTitle}>🐄 Animals</div>
      <div style={styles.cardsGrid}>
        <StatCard icon="🐄" label="Total Animals" value={stats.totalAnimals} color="#3498db" bgColor="#e3f2fd" onClick={() => setActiveTab('animals')} />
        <StatCard icon="🥛" label="Milking Animals" value={stats.milkingAnimals} color="#9b59b6" bgColor="#f3e5f5" onClick={() => setActiveTab('animals')} />
      </div>

      {/* ── SECTION 2: Today's Production ── */}
      <div style={styles.sectionTitle}>🥛 Today's Production</div>
      <div style={styles.cardsGrid}>
        <StatCard icon="🥛" label="Total Milk Today" value={`${stats.todayProduction.toFixed(1)} L`} color="#1b5e20" bgColor="#e8f5e9" onClick={() => setActiveTab('production')} />
      </div>

      {/* ── SECTION 3: Finance ── */}
      <div style={styles.sectionTitle}>💰 This Month's Finance</div>
      <div style={styles.cardsGrid}>
        <StatCard icon="💰" label="Monthly Revenue" value={formatCurrency(stats.monthlyRevenue)} color="#27ae60" bgColor="#e8f5e9" onClick={() => setActiveTab('finance')} />
        <StatCard icon="💸" label="Monthly Expenses" value={formatCurrency(stats.monthlyExpenditure)} color="#e74c3c" bgColor="#ffebee" onClick={() => setActiveTab('expenditure')} />
        <StatCard
          icon={stats.netProfit >= 0 ? '📈' : '📉'}
          label={stats.netProfit >= 0 ? 'Profit' : 'Loss'}
          value={formatCurrency(Math.abs(stats.netProfit))}
          color={stats.netProfit >= 0 ? '#27ae60' : '#e74c3c'}
          bgColor={stats.netProfit >= 0 ? '#e8f5e9' : '#ffebee'}
          onClick={() => setActiveTab('finance')}
        />
      </div>

      {/* ── SECTION 4: Action Required (only show if there's something due) ── */}
      {(stats.checkupDue > 0 || stats.overdueVaccinations > 0) && (
        <>
          <div style={styles.sectionTitle}>⚠️ Action Required</div>
          <div style={styles.cardsGrid}>
            {stats.checkupDue > 0 && (
              <StatCard
                icon="📋"
                label="Checkup Due"
                value={stats.checkupDue}
                color="#f39c12"
                bgColor="#fff3e0"
                onClick={() => setActiveTab('vaccinations')}
              />
            )}
            {stats.overdueVaccinations > 0 && (
              <StatCard
                icon="🚨"
                label="Overdue Vaccinations"
                value={stats.overdueVaccinations}
                color="#e74c3c"
                bgColor="#ffebee"
                onClick={() => setActiveTab('vaccinations')}
              />
            )}
          </div>
        </>
      )}

      {/* ── Quick Summary ── */}
      <div style={styles.summaryBox}>
        <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '20px', color: '#2c3e50' }}>
          Quick Summary
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>

          <div style={styles.summaryItem}>
            <span style={styles.summaryLabel}>Total Outstanding Amount of Customers Till Today</span>
            <div style={{ fontSize: '22px', fontWeight: '700', color: stats.totalOutstanding > 0 ? '#e67e22' : '#27ae60' }}>
              {formatCurrency(stats.totalOutstanding)}
            </div>
          </div>

          <div style={styles.summaryItem}>
            <span style={styles.summaryLabel}>This Month Expenditure</span>
            <div style={{ fontSize: '22px', fontWeight: '700', color: '#e74c3c' }}>
              {formatCurrency(stats.monthlyExpenditure)}
            </div>
          </div>

          <div style={styles.summaryItem}>
            <span style={styles.summaryLabel}>Farm Profitability</span>
            <div style={{ fontSize: '22px', fontWeight: '700', color: stats.netProfit >= 0 ? '#27ae60' : '#e74c3c' }}>
              {stats.netProfit >= 0 ? '✅ Profitable' : '❌ Loss'}
            </div>
            <div style={{ fontSize: '14px', color: '#7f8c8d', marginTop: '4px' }}>
              {formatCurrency(Math.abs(stats.netProfit))} {stats.netProfit >= 0 ? 'earned' : 'deficit'}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

// Reusable stat card
const StatCard = ({ icon, label, value, color, bgColor, onClick }) => (
  <div
    style={{
      background: 'white',
      padding: '24px',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      borderLeft: `4px solid ${color}`,
      transition: 'transform 0.2s, box-shadow 0.2s',
      cursor: onClick ? 'pointer' : 'default',
    }}
    onClick={onClick}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-4px)';
      e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.12)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
      <div style={{
        width: '56px', height: '56px',
        borderRadius: '12px',
        background: bgColor,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '28px',
        flexShrink: 0,
      }}>
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '13px', color: '#7f8c8d', marginBottom: '4px', fontWeight: '500' }}>
          {label}
        </div>
        <div style={{ fontSize: '24px', fontWeight: '700', color: '#2c3e50' }}>
          {value}
        </div>
      </div>
    </div>
  </div>
);

const styles = {
  sectionTitle: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#7f8c8d',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '12px',
    marginTop: '28px',
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '16px',
  },
  summaryBox: {
    marginTop: '28px',
    background: 'white',
    padding: '24px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  summaryItem: {
    padding: '16px',
    background: '#f8f9fa',
    borderRadius: '10px',
  },
  summaryLabel: {
    display: 'block',
    fontSize: '13px',
    color: '#7f8c8d',
    marginBottom: '8px',
    fontWeight: '500',
  },
  refreshButton: {
    padding: '10px 20px',
    background: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
  },
  retryButton: {
    marginTop: '20px',
    padding: '12px 24px',
    background: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
  },
};

export default DashboardTab;